import { LoadingOutlined } from "@ant-design/icons";
import { Alert, AutoComplete, Button, Col, Form, Input, message, Row, Select, Switch } from "antd";
import { useState } from "react";
import Icon from "../../../assets/icons/icon";
import EnumSelect from "../../../components/form/enumSelect";
import InputPrice from "../../../components/form/inputPrice";
import LoadingContainer from "../../../components/loadingContainer";
import { renderErrorMessage } from "../../../components/messages/errorMessage";
import _t from "../../../lang/translate";
import { deleteModelBrowserCacheKey, storeCustomVariant } from "../../../services/motorAtlasService";
import { CreateCustomVariantForm, ModelMatch } from "../../../types/motorAtlasCatalogTypes";
import useMotorAtlasOptions from "./useMotorAtlasOptions";
import { clearDependentFields, createEmptyModelMatch, SubmitPayload } from "./util";

const CURRENT_YEAR = new Date().getFullYear();

type MotorAtlasCreateCustomVariantProps = {
  submit: (submitPayload: SubmitPayload) => void;
}

export default function MotorAtlasCreateCustomVariant({ submit }: MotorAtlasCreateCustomVariantProps) {
  const [form] = Form.useForm<CreateCustomVariantForm>()
  const { handleDrillDown, loadingOptionsFields, options, rawOptions } = useMotorAtlasOptions("autocomplete", false)
  const [loading, setLoading] = useState(false)

  async function handleSubmit(values: CreateCustomVariantForm) {
    try {
      setLoading(true)
      const { data } = await storeCustomVariant(values)
      const customMotorAtlasId = data.eCode;
      if (customMotorAtlasId) {
        message.success(_t("custom_variant_created", "with_id") + " " + customMotorAtlasId)
        submit({ selectedMatch: createEmptyModelMatch(customMotorAtlasId) })
        const makeEcode = rawOptions.current.make.find(option => option.label === values.make)?.value
        deleteModelBrowserCacheKey({
          type: values.type,
          make: makeEcode,
        })
      }
    } catch (error) {
      renderErrorMessage(error)
    } finally {
      setLoading(false)
    }
  }

  function applyFilterPipeline(rawValues: CreateCustomVariantForm, changedKey?: keyof CreateCustomVariantForm) {
    const cleaned = clearDependentFields<CreateCustomVariantForm>(rawValues, changedKey as any, undefined)
    form.setFieldsValue(cleaned);
    handleDrillDown(cleaned, changedKey)
  }

  function handleValuesChange(changed: Partial<CreateCustomVariantForm>, all: CreateCustomVariantForm) {
    const key = Object.keys(changed)[0] as keyof CreateCustomVariantForm
    applyFilterPipeline(all, key);
  }


  return (
    <LoadingContainer loading={loading} showLogo>
      <Alert closable type="info"
        message={"Vær opmærksom på dubletter"}
        description={"Når du opretter en model, bør du tjekke om den allerede eksisterer i systemet."}
        className="mb-1"
      />
      <h3 className="mb-1">{_t("create", "new") + " " + _t("model").toLowerCase()}</h3>

      <Form
        form={form}
        labelAlign="left"
        labelCol={{ flex: "0 0 120px" }}
        className="motor_atlas_catalog_mapper_form"
        colon={false}
        onFinish={handleSubmit}
        onValuesChange={handleValuesChange}
      >
        <Form.Item name={"type"} label={_t("type")} rules={[{ required: true }]}>
          <Select allowClear options={[
            { value: "01", label: "Personbil" },
            { value: "02", label: "Varebil" },
            { value: "03", label: "Motorcykel" },
          ]} />
        </Form.Item>
        <Form.Item label={_t("brand")} name={['make']} rules={[{ required: true }]}>
          <AutoComplete
            showSearch
            placeholder={_t("choose")}
            optionFilterProp="label"
            filterOption={(input, option) =>
              (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
            }
            dropdownMatchSelectWidth={400}
            allowClear
            options={options.make}>

            <Input suffix={loadingOptionsFields.has("make") ? (<LoadingOutlined />) : <Icon name="chevron-down-outline" />} />
          </AutoComplete>
        </Form.Item>

        <Form.Item label={_t("model")} name={['model']} rules={[{ required: true }]}>
          <AutoComplete
            showSearch
            placeholder={_t("choose")}
            optionFilterProp="label"
            dropdownMatchSelectWidth={400}
            filterOption={(input, option) =>
              (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
            }
            allowClear
            options={options.model}>
            <Input suffix={loadingOptionsFields.has("model") ? (<LoadingOutlined />) : <Icon name="chevron-down-outline" />} />
          </AutoComplete>
        </Form.Item>
        <Form.Item label={_t("subModel")} name={['subModel']} rules={[{ required: true }]}>
          <AutoComplete
            showSearch
            placeholder={_t("choose")}
            optionFilterProp="label"
            dropdownMatchSelectWidth={400}
            filterOption={(input, option) =>
              (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
            }
            allowClear
            options={options.subModel} >
            <Input suffix={loadingOptionsFields.has("subModel") ? (<LoadingOutlined />) : <Icon name="chevron-down-outline" />} />
          </AutoComplete>
        </Form.Item>
        <Form.Item label={_t("variant")} name={['variant']} rules={[{ required: true }]}>
          <AutoComplete
            showSearch
            placeholder={_t("choose")}
            optionFilterProp="label"
            dropdownMatchSelectWidth={400}
            filterOption={(input, option) =>
              (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
            }
            allowClear
            options={options.variant}>
            <Input suffix={loadingOptionsFields.has("variant") ? (<LoadingOutlined />) : <Icon name="chevron-down-outline" />} />
          </AutoComplete>
        </Form.Item>

        <Form.Item name={['productionYear']} label={_t("production_years")} rules={[{ required: true }]}>
          <Select showSearch placeholder={_t("choose")} optionFilterProp="label" options={generateNumberedOptions(1980, CURRENT_YEAR)} />
        </Form.Item>

        <Row gutter={[8, 8]}>
          <Col flex={"1 1 0%"}>
            <Form.Item name={"body"} label={_t("trim")} rules={[{ required: true }]}>
              <EnumSelect nameKey={['vehicle', 'body']} />
            </Form.Item>
          </Col>
          <Col flex={"1 1 0%"}>
            <Form.Item name={"drive"} label={_t("drive_wheel")} rules={[{ required: true }]}>
              <EnumSelect nameKey={['vehicle', 'driveType']} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={[8, 8]}>
          <Col flex={"1 1 0%"}>
            <Form.Item label={_t("number_of_seats")} name={['seats']} rules={[{ required: true }]}>
              <Select showSearch placeholder={_t("choose")} optionFilterProp="label" options={generateNumberedOptions(2, 9)} />
            </Form.Item>
          </Col>
          <Col flex={"1 1 0%"}>
            <Form.Item label={_t("number_of_doors")} name={['doors']} rules={[{ required: true }]}>
              <Select showSearch placeholder={_t("choose")} optionFilterProp="label" options={generateNumberedOptions(2, 6)} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={[8, 8]}>
          <Col flex={"1 1 0%"}>
            <Form.Item label={_t("engine_size")} name={['engineSize']} rules={[{ required: true }]}>
              <Input suffix={"ccm."} placeholder={"fx 2993"} />
            </Form.Item>
          </Col>
          <Col flex={"1 1 0%"}>
            <Form.Item label={_t("horse_power")} name={['horsePower']} rules={[{ required: true }]}>
              <Input placeholder={"fx 245"} />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item label={_t("cylinders")} name={['cylinders']} rules={[{ required: true }]}>
          <Select showSearch placeholder={_t("choose")} optionFilterProp="label" options={generateNumberedOptions(0, 12)} />
        </Form.Item>
        <Row gutter={[8, 8]}>
          <Col flex={"1 1 0%"}>

            <Form.Item name={"fuel"} label={_t("fuel")} rules={[{ required: true }]}>
              <EnumSelect nameKey={['vehicle', 'fuel']} />
            </Form.Item>
          </Col>
          <Col flex={"1 1 0%"}>

            <Form.Item name={"gearbox"} label={_t("gearbox")} rules={[{ required: true }]}>
              <EnumSelect nameKey={['vehicle', 'gearbox']} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={[8, 8]}>
          <Col flex={"1 1 0%"}>
            <Form.Item label={_t("battery_capacity")} name={['batteryCapacity']} rules={[{ required: true }]}>
              <InputPrice placeholder="fx 77" suffix={_t("battery_unit")} />
            </Form.Item>
          </Col>
          <Col flex={"1 1 0%"}>
            <Form.Item label={"CO2 g/km"} name={['emissionAmount']} rules={[{ required: true }]}>
              <InputPrice placeholder="fx 129" suffix={"g/km."} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={[8, 8]}>
          <Col flex={"1 1 0%"}>
            <Form.Item label={_t("range", "minimum _short")} name={['electricRangeMin']} rules={[{ required: true }]}>
              <InputPrice placeholder="fx 390" suffix={"km"} />
            </Form.Item>
          </Col>
          <Col flex={"1 1 0%"}>
            <Form.Item label={_t("range", "maximum _short")} name={['electricRangeMax']} rules={[{ required: true }]}>
              <InputPrice placeholder="fx 430" suffix={"km"} />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item name={"forCommercial"} valuePropName="checked" label={"Erhvervsbil"}>
          <Switch />
        </Form.Item>
        <Form.Item label={_t("note")} name={['note']}>
          <Input.TextArea placeholder={"Skrive en note"} />
        </Form.Item>
        <Button style={{ marginLeft: "auto", display: "block" }}
          type="primary"
          icon={<Icon name={"add-outline"} />}
          htmlType="submit"
        >
          {_t("create", "model")}
        </Button>
      </Form>
    </LoadingContainer>
  )
}


function generateNumberedOptions(min: number, max: number) {
  const options = []
  for (let i = max; i >= min; i--) {
    options.push({ label: i, value: i })
  }
  return options
}
